import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import backendApi from '../services/backendApi';

interface User {
  userId?: number | string | null;
  username: string;
  fullName: string;
  role: string;
  roleName?: string;
  token: string;
  isGlobalAdmin?: boolean;
  tenantName?: string;
  tenant?: string;
  siteId?: number | string | null;
  siteCode?: string | null;
  email?: string;
  phone?: string;
  company?: string;
  companyId?: number | string | null;
  department?: string;
  avatar?: string;
  joinDate?: string;
  lastLogin?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isGlobalAdmin: boolean;
  login: (userData: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [skipNextValidation, setSkipNextValidation] = useState(false);

  // Helper function to load avatar from localStorage
  const getCachedAvatar = (userIdentifier: string | null): string | undefined => {
    if (!userIdentifier) return undefined;

    // Try user-specific localStorage key
    let cachedAvatar = localStorage.getItem(`userAvatar_${userIdentifier}`);

    // Try user-specific profile
    if (!cachedAvatar) {
      try {
        const profile = JSON.parse(localStorage.getItem(`userProfile_${userIdentifier}`) || '{}');
        cachedAvatar = profile.avatar || null;
      } catch {
        // Ignore parsing errors
      }
    }

    return cachedAvatar || undefined;
  };

  useEffect(() => {
    let validationInterval: ReturnType<typeof setInterval> | null = null;
    let isInitialCheck = true;
    let consecutiveFailures = 0;
    const MAX_CONSECUTIVE_FAILURES = 3; // Only logout after 3 consecutive failures

    const checkAuth = async () => {
      try {
        // Read auth token from session storage only
        const token = sessionStorage.getItem('access_token');
        const username = sessionStorage.getItem('username');
        const fullName = sessionStorage.getItem('full_name');
        const role = sessionStorage.getItem('user_role');
        const roleName = sessionStorage.getItem('role_name');
        const isGlobalAdmin = sessionStorage.getItem('isGlobalAdmin') === 'true';
        const tenantName = sessionStorage.getItem('tenant_name');
        const tenant = sessionStorage.getItem('tenant');
        const email = sessionStorage.getItem('email');
        const siteIdRaw = sessionStorage.getItem('site_id');
        const cachedSiteId =
          siteIdRaw && !Number.isNaN(Number(siteIdRaw)) ? Number(siteIdRaw) : null;

        // If no token exists, user is not logged in
        if (!token) {
          setUser(null);
          setIsLoading(false);
          return;
        }

        // If we have a token but no username/role, still try to validate with backend
        // (might be a fresh token from login)
        if (token) {
          // Skip validation if it was just set by login (to avoid race condition)
          if (skipNextValidation) {
            setSkipNextValidation(false);
            // Use cached credentials without validation (fresh login)
            setUser({
              token,
              username: username || '',
              fullName: fullName || username || '',
              role: role || 'user',
              isGlobalAdmin,
              tenantName: tenantName || 'AxonMetric',
              tenant: tenant || '',
              siteId: cachedSiteId,
              siteCode: tenant || null,
              email: email || '',
              avatar: getCachedAvatar(username)
            });
            return;
          }

          // Only validate user exists in database on initial check and periodically
          // For subsequent checks, allow cached credentials to work (less aggressive)
          const shouldValidate = isInitialCheck || consecutiveFailures > 0;

          if (shouldValidate) {
            try {
              const response = await backendApi.getCurrentUser();

              if (response.success && response.user) {
                // User exists and is valid - use backend user data
                consecutiveFailures = 0; // Reset failure count on success
                const backendUser = response.user;

                // Update sessionStorage with fresh user data
                if (backendUser.username) sessionStorage.setItem('username', backendUser.username);
                if (backendUser.first_name && backendUser.last_name) {
                  sessionStorage.setItem('full_name', `${backendUser.first_name} ${backendUser.last_name}`);
                }
                if (backendUser.role) sessionStorage.setItem('user_role', backendUser.role);
                if (backendUser.role_name) sessionStorage.setItem('role_name', backendUser.role_name);
                sessionStorage.setItem('isGlobalAdmin', String(backendUser.is_superuser || false));
                if (backendUser.email) sessionStorage.setItem('email', backendUser.email);

                // Determine if user has admin privileges to view multiple contexts
                const backendUserRole = (backendUser.role || '').toLowerCase().replace(' ', '_');
                const backendRoleName = (backendUser.role_name || '').toLowerCase().replace(' ', '_');
                const isSuperUser = !!(backendUser.is_superuser || backendUser.is_super_admin);

                const isAdmin = isSuperUser ||
                  backendUserRole.includes('admin') ||
                  backendUserRole === 'ho' ||
                  backendRoleName.includes('admin') ||
                  backendRoleName === 'ho' ||
                  backendUser.role === 'admin' ||
                  backendUser.role === 'system_admin' ||
                  backendUser.role === 'ho_admin';

                // For Admins, preserve the current selected context from session/local storage
                // instead of overwriting with the database-assigned default site.
                const currentTenant = sessionStorage.getItem('tenant') || localStorage.getItem('currentSite');
                const currentSiteId = sessionStorage.getItem('site_id') || localStorage.getItem('site_id');
                const currentCompanyId = sessionStorage.getItem('company_id') || localStorage.getItem('company_id');

                // If user is admin and we HAVE a current tenant/company in storage, use that.
                // Otherwise, use the backend-assigned site.
                const hasSelectedContext = !!(currentTenant || currentSiteId || currentCompanyId);
                const resolvedSiteCode = (isAdmin && hasSelectedContext) ? currentTenant : (backendUser.site?.site_code || backendUser.site_code || '');
                const resolvedSiteId = (isAdmin && hasSelectedContext) ? currentSiteId : (backendUser.site?.site_id || backendUser.site_id || null);
                const resolvedCompanyId = (isAdmin && hasSelectedContext) ? currentCompanyId : (backendUser.company_id || backendUser.site?.company_id || null);

                if (resolvedSiteCode) {
                  sessionStorage.setItem('tenant', resolvedSiteCode);
                  localStorage.setItem('currentSite', resolvedSiteCode);
                }
                if (resolvedSiteId) {
                  sessionStorage.setItem('site_id', String(resolvedSiteId));
                  localStorage.setItem('site_id', String(resolvedSiteId));
                }
                if (resolvedCompanyId) {
                  sessionStorage.setItem('company_id', String(resolvedCompanyId));
                  localStorage.setItem('company_id', String(resolvedCompanyId));
                }
                if (backendUser.user_id || backendUser.id) {
                  sessionStorage.setItem('user_id', String(backendUser.user_id || backendUser.id));
                }

                // Get avatar from multiple sources
                // Get user identifier for user-specific storage
                const userIdentifier = backendUser.username || backendUser.id || username;

                // Get avatar from backend first, then fallback to user-specific localStorage
                const userAvatar = backendUser.avatar ||
                  backendUser.profile_picture ||
                  (userIdentifier ? localStorage.getItem(`userAvatar_${userIdentifier}`) : null) ||
                  (() => {
                    if (userIdentifier) {
                      try {
                        const profile = JSON.parse(localStorage.getItem(`userProfile_${userIdentifier}`) || '{}');
                        return profile.avatar || null;
                      } catch {
                        return null;
                      }
                    }
                    return null;
                  })() ||
                  null;

                // Store avatar in user-specific localStorage for persistence
                if (userAvatar && userIdentifier) {
                  localStorage.setItem(`userAvatar_${userIdentifier}`, userAvatar);
                  try {
                    const profile = JSON.parse(localStorage.getItem(`userProfile_${userIdentifier}`) || '{}');
                    profile.avatar = userAvatar;
                    localStorage.setItem(`userProfile_${userIdentifier}`, JSON.stringify(profile));
                  } catch {
                    localStorage.setItem(`userProfile_${userIdentifier}`, JSON.stringify({ avatar: userAvatar }));
                  }
                }

                setUser({
                  token,
                  username: backendUser.username || username || '',
                  fullName: backendUser.first_name && backendUser.last_name
                    ? `${backendUser.first_name} ${backendUser.last_name}`
                    : fullName || backendUser.username || '',
                  role: backendUser.role || role || 'user',
                  roleName: backendUser.role_name || roleName || backendUser.role || undefined,
                  userId: backendUser.user_id || backendUser.id || null,
                  isGlobalAdmin: backendUser.is_superuser || isGlobalAdmin,
                  tenantName: tenantName || 'AxonMetric',
                  tenant: resolvedSiteCode || tenant || '',
                  siteId: resolvedSiteId,
                  siteCode: resolvedSiteCode || null,
                  email: backendUser.email || email || '',
                  avatar: userAvatar,
                  phone: backendUser.phone || '',
                  companyId: resolvedCompanyId,
                  department: backendUser.department || ''
                });
              } else if (
                response.error &&
                (
                  response.error.includes('credentials') ||
                  response.error.toLowerCase().includes('token not valid') ||
                  response.error.toLowerCase().includes('token type') ||
                  response.error.toLowerCase().includes('authentication')
                )
              ) {
                // Authentication credentials error - token is invalid
                consecutiveFailures++;
                if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES || isInitialCheck) {
                  setUser(null);
                  sessionStorage.removeItem('access_token');
                  sessionStorage.clear();
                } else {
                  // Keep cached credentials for now
                  if (username && role) {
                    setUser({
                      token,
                      username: username || '',
                      fullName: fullName || username || '',
                      role: role || 'user',
                      isGlobalAdmin,
                      tenantName: tenantName || 'AxonMetric',
                      tenant: tenant || '',
                      siteId: cachedSiteId,
                      siteCode: tenant || null,
                      email: email || '',
                      avatar: getCachedAvatar(username)
                    });
                  }
                }
              } else {
                // Check if this is a connection error
                const isConnectionError = response.error && (
                  response.error.includes('Failed to fetch') ||
                  response.error.includes('ERR_CONNECTION_REFUSED') ||
                  response.error.includes('CONNECTION_REFUSED') ||
                  response.error.includes('NetworkError') ||
                  response.error.includes('timeout')
                );

                const errText = String(response.error || '').toLowerCase();
                const isRateLimitError =
                  errText.includes('too many requests') ||
                  errText.includes('rate limit') ||
                  errText.includes('too_many_requests') ||
                  errText.includes('429');

                if (isConnectionError || isRateLimitError) {
                  // Server unavailable - use cached credentials silently
                  consecutiveFailures = 0; // Don't count connection errors as failures
                  if (username && role) {
                    setUser({
                      token,
                      username: username || '',
                      fullName: fullName || username || '',
                      role: role || 'user',
                      isGlobalAdmin,
                      tenantName: tenantName || 'AxonMetric',
                      tenant: tenant || '',
                      siteId: cachedSiteId,
                      siteCode: tenant || null,
                      email: email || '',
                      avatar: getCachedAvatar(username)
                    });
                  }
                } else {
                  // User doesn't exist in database or other error
                  consecutiveFailures++;
                  if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES || isInitialCheck) {
setUser(null);
                    sessionStorage.removeItem('access_token');
                    sessionStorage.clear();
                  } else {
                    // Keep user logged in but with cached data on temporary failures
                    if (username && role) {
                      setUser({
                        token,
                        username: username || '',
                        fullName: fullName || username || '',
                        role: role || 'user',
                        isGlobalAdmin,
                        tenantName: tenantName || 'AxonMetric',
                        tenant: tenant || '',
                        siteId: cachedSiteId,
                        siteCode: tenant || null,
                        email: email || '',
                        avatar: getCachedAvatar(username)
                      });
                    }
                  }
                }
              }
            } catch (error: any) {
              consecutiveFailures++;

              // Handle connection errors gracefully
              if (error.message === 'SERVER_NOT_AVAILABLE' || error.code === 'CONNECTION_REFUSED') {
                // If server is down, allow access with stored credentials (offline mode)
                consecutiveFailures = 0; // Don't count connection errors as failures
                if (username && role) {
                  setUser({
                    token,
                    username: username || '',
                    fullName: fullName || username || '',
                    role: role || 'user',
                    isGlobalAdmin,
                    tenantName: tenantName || 'AxonMetric',
                    tenant: tenant || '',
                    siteId: cachedSiteId,
                    siteCode: tenant || null,
                    email: email || '',
                    avatar: getCachedAvatar(username)
                  });
                }
              } else {
                const errorText = String(error?.message || '').toLowerCase();
                const isRateLimitError =
                  errorText.includes('too many requests') ||
                  errorText.includes('rate limit') ||
                  errorText.includes('too_many_requests') ||
                  errorText.includes('429');

                if (isRateLimitError) {
                  consecutiveFailures = 0;
                  if (username && role) {
                    setUser({
                      token,
                      username: username || '',
                      fullName: fullName || username || '',
                      role: role || 'user',
                      isGlobalAdmin,
                      tenantName: tenantName || 'AxonMetric',
                      tenant: tenant || '',
                      siteId: cachedSiteId,
                      siteCode: tenant || null,
                      email: email || '',
                      avatar: getCachedAvatar(username)
                    });
                  }
                  return;
                }

                // For authentication errors (401, 403), increment failure count
                if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES || isInitialCheck) {
                  // Only logout if initial check or after multiple failures
setUser(null);
                  sessionStorage.removeItem('access_token');
                  sessionStorage.clear();
                } else {
                  // Keep user logged in on temporary failures if we have cached data
                  if (username && role) {
                    setUser({
                      token,
                      username: username || '',
                      fullName: fullName || username || '',
                      role: role || 'user',
                      isGlobalAdmin,
                      tenantName: tenantName || 'AxonMetric',
                      tenant: tenant || '',
                      siteId: cachedSiteId,
                      siteCode: tenant || null,
                      email: email || '',
                      avatar: getCachedAvatar(username)
                    });
                  }
                }
              }
            }
          } else {
            // No validation needed - use cached credentials if available
            if (username && role) {
              setUser({
                token,
                username: username || '',
                fullName: fullName || username || '',
                role: role || 'user',
                isGlobalAdmin,
                tenantName: tenantName || 'AxonMetric',
                tenant: tenant || '',
                siteId: cachedSiteId,
                siteCode: tenant || null,
                email: email || '',
                avatar: getCachedAvatar(username)
              });
            } else {
              // No cached credentials, but we have a token - try to validate
              try {
                const response = await backendApi.getCurrentUser();
                if (response.success && response.user) {
                  const backendUser = response.user;
                  setUser({
                    token,
                    username: backendUser.username || '',
                    fullName: backendUser.first_name && backendUser.last_name
                      ? `${backendUser.first_name} ${backendUser.last_name}`
                      : backendUser.username || '',
                    role: backendUser.role || 'user',
                    isGlobalAdmin: backendUser.is_superuser || false,
                    tenantName: tenantName || 'AxonMetric',
                    tenant: backendUser.site?.site_code || backendUser.site_code || tenant || '',
                    email: backendUser.email || ''
                  });
                } else {
                  setUser(null);
                }
              } catch (error) {
                setUser(null);
              }
            }
          }
        } else {
          // No token - user is not logged in
          setUser(null);
        }

        isInitialCheck = false;
      } catch (error) {
        console.error('Error checking authentication:', error);
        // Don't clear user on unexpected errors - keep them logged in
        if (isInitialCheck) {
          setUser(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Initial check
    checkAuth();

    // Set up periodic validation (every 15 minutes - less frequent)
    // This prevents too many API calls and reduces interruptions
    validationInterval = setInterval(checkAuth, 15 * 60 * 1000);

    return () => {
      if (validationInterval) {
        clearInterval(validationInterval);
      }
    };
  }, [skipNextValidation]);

  const login = (userData: User) => {
    const normalizedToken = userData.token ? String(userData.token).replace(/^Bearer\s+/i, '').trim() : '';

    setUser(userData);
    // Store token in session storage to reduce persistence exposure
    if (normalizedToken) {
      sessionStorage.setItem('access_token', normalizedToken);
    } else {
      sessionStorage.removeItem('access_token');
    }
    sessionStorage.setItem('username', userData.username);
    sessionStorage.setItem('full_name', userData.fullName || userData.username);
    sessionStorage.setItem('user_role', userData.role);
    sessionStorage.setItem('role_name', userData.roleName || userData.role || '');
    sessionStorage.setItem('isGlobalAdmin', String(userData.isGlobalAdmin || false));
    if (userData.userId !== undefined && userData.userId !== null) {
      sessionStorage.setItem('user_id', String(userData.userId));
    }
    if (userData.companyId !== undefined && userData.companyId !== null) {
      sessionStorage.setItem('company_id', String(userData.companyId));
      localStorage.setItem('company_id', String(userData.companyId));
    }
    sessionStorage.setItem('tenant_name', userData.tenantName || 'AxonMetric');
    sessionStorage.setItem('tenant', userData.tenant || '');
    if (userData.siteCode) {
      localStorage.setItem('currentSite', userData.siteCode);
    }
    if (userData.siteId !== undefined && userData.siteId !== null) {
      sessionStorage.setItem('site_id', String(userData.siteId));
      localStorage.setItem('site_id', String(userData.siteId));
    }
    sessionStorage.setItem('email', userData.email || '');
    // Skip validation on next check since we just logged in successfully
    setSkipNextValidation(true);
  };

  const logout = () => {
    void backendApi.logout();
    setUser(null);
    // Clear both localStorage and sessionStorage
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('username');
    sessionStorage.removeItem('full_name');
    sessionStorage.removeItem('user_role');
    sessionStorage.removeItem('role_name');
    sessionStorage.removeItem('user_id');
    sessionStorage.removeItem('company_id');
    localStorage.removeItem('company_id');
    sessionStorage.removeItem('isGlobalAdmin');
    sessionStorage.removeItem('tenant_name');
    sessionStorage.removeItem('tenant');
    sessionStorage.removeItem('site_id');
    localStorage.removeItem('site_id');
    localStorage.removeItem('currentSite');
    sessionStorage.removeItem('email');
    localStorage.removeItem('rememberedUsername');
    localStorage.removeItem('lastActivity');
  };

  // Automatic inactivity logout (2 hours of inactivity)
  useEffect(() => {
    if (!user) {
      localStorage.removeItem('lastActivity');
      return;
    }

    const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
    const CHECK_INTERVAL = 10 * 1000; // Check every 10 seconds

    const updateActivity = () => {
      localStorage.setItem('lastActivity', Date.now().toString());
    };

    // Set initial activity timestamp if not present
    if (!localStorage.getItem('lastActivity')) {
      updateActivity();
    }

    // Set up event listeners for user activity
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // Throttle updates to avoid excessive localStorage writes (max once every 2 seconds)
    let lastWriteTime = 0;
    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastWriteTime > 2000) {
        updateActivity();
        lastWriteTime = now;
      }
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    // Periodically check for inactivity
    const interval = setInterval(() => {
      const lastActivityStr = localStorage.getItem('lastActivity');
      if (lastActivityStr) {
        const lastActivity = Number(lastActivityStr);
        if (!isNaN(lastActivity)) {
          const inactiveDuration = Date.now() - lastActivity;
          if (inactiveDuration >= INACTIVITY_TIMEOUT) {
logout();
          }
        }
      } else {
        // If lastActivity is somehow missing but user is logged in, initialize it
        updateActivity();
      }
    }, CHECK_INTERVAL);

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      clearInterval(interval);
    };
  }, [user]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isGlobalAdmin: user?.isGlobalAdmin || false,
    isLoading,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

